import {
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { Control, Controller } from "react-hook-form";
import { debounce, isEmpty } from "lodash";
import dayjs from "dayjs";

import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { rules } from "../../../utils/validation/Validation";
import {
  displayFlex,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import { Discipline, ListOptions } from "../../../models/Master";
import DatePicker from "../../../components/FormPriorAuthReAuth/DatePicker";
import {
  addDaysToDate,
  getSelectedTimeForSOC,
  getValue,
  is21OrTurning21Soon,
  isAbove21,
} from "../../../utils";
import { MyAccountModel } from "../../../models/MyAccount";
import { updateTabStatus } from "../../../constants/TabSessionStorage";
import {
  DisciplineTableHeader,
  Numbers,
  SlotsDispatchTypes,
} from "../../../constants/Referral";
import { TextConstants } from "../../../constants/Patient";
import {
  MemberInfoData,
  PatientInformation,
  PatientInformationView,
} from "../../../models/Patient";
import {
  ReAuthDefaultReferralDetails,
  Slots,
  SlotsPayload,
} from "../../../models/Referral";
import { DateFormats } from "../../../constants/Date";
import { formatDate } from "../../../utils/DateTime";
import {
  FaExclamationIcon,
  FieldSpacing,
  SectionSpacing,
  StyleGridRow,
} from "../../PriorAuthWorkFlow/styles/style";
import { DisciplineTable } from "./DisciplineTable";
import { InformationText } from "../../../app/common/TextLabel";
import { RadioGroupStyle } from "../styles/style";
import { getType } from "../../../constants/eligibility";
import { ReAuthTabData } from "../../../models/TabSessionStorage";
import { DisciplineListPayload } from "../../../models/PhysicianDetails";
import { BCBSMI, BCBSMIMINSOCDATE } from "../../../constants/FunctionalUtils";

export interface PropsFromState {
  control: Control<any>;
  setValue: any;
  tabSessionStorageReAuthState: ReAuthTabData;
  masterCareTypeState: ListOptions[];
  disciplineListState: Discipline[];
  reimbursementTypeId: number;
  loggedInUserState: MyAccountModel;
  reset: any;
  getValues: any;
  informationState: PatientInformationView;
  slotsState: Slots;
  patientInformationByIdState: PatientInformation;
  memberInfoDataState: MemberInfoData;
  reAuthReferralDetailState: ReAuthDefaultReferralDetails;
  disciplineStatusState: DisciplineListPayload;
}
export interface PropsFromDispatch {
  getCareTypes: () => void;
  setValue: any;
  getDisciplineList: (
    eligibilityId: number,
    isPortal: boolean,
    type: number
  ) => void;
  getSlots: (payload: SlotsPayload) => void;
  resetStates: (actionType: string, field?: string) => void;
  getDisciplineStatusList: (referralId: string) => void;
}
type AllProps = PropsFromState & PropsFromDispatch;

const RequestServices: React.FC<AllProps> = ({
  control,
  disciplineListState,
  getDisciplineList,
  setValue,
  loggedInUserState,
  getValues,
  reset,
  patientInformationByIdState,
  getSlots,
  reimbursementTypeId,
  slotsState,
  memberInfoDataState,
  resetStates,
  reAuthReferralDetailState,
  tabSessionStorageReAuthState,
  getDisciplineStatusList,
  disciplineStatusState
}) => {
  const [stWarning, setSTWarning] = React.useState<boolean>(false);
  const [dateAdmit, setDateAdmit] = React.useState<boolean>(false);
  const [memberVisit, setMemberVisit] = React.useState<boolean>(false);

  const [disciplineOptions, setDisciplineOptions] = React.useState<
    Discipline[]
  >([]);

  const callSlots = (date: any) => {
    getSlots({
      patientEligibilityId: patientInformationByIdState?.patientEligibilityId,
      type: Numbers.TWO,
      startOfCareDate: formatDate(dayjs(date).format(DateFormats.YYYY_MM_DD), DateFormats.YYYY_MM_DD, DateFormats.YYYY_MM_DD, true),
      referralId: reAuthReferralDetailState?.referralId,
      reimbursementTypeId:
        isEmpty(reimbursementTypeId) || reimbursementTypeId === Numbers.ZERO
          ? ""
          : reimbursementTypeId,
    });
  };

  useEffect(() => {
    if (typeof getDisciplineList === "function") {
      getDisciplineList(1, true, 2);
    }
    const email = getValue(loggedInUserState, "providerPortalUser.email");
    setValue("requestService.userCreated", email);
    setValue("requestService.fullNameCreated", email);
    setValue("requestService.psychologicalDiseasesDto", null);
    setValue("requestService.isNurseToOpen", false);
    if (checkIsBcbsTn() && typeof getDisciplineStatusList === "function") {
      getDisciplineStatusList(getValue(reAuthReferralDetailState, "referralId").toString());
    }
  }, []);

  useEffect(() => {
    const socDate = formatDate(
        reAuthReferralDetailState.startOfCare,
        DateFormats.MM_DD_YYYY
      )
    setDateAdmit(getValues().requestService.memberAdmitted === "yes");
    setMemberVisit(getValues().requestService.memberVisit === "yes");
    if (!isEmpty(getValue(tabSessionStorageReAuthState, "requestService.startOfCare")) && getValue(tabSessionStorageReAuthState, "requestService.startOfCare", "").length > 0) {
      return;
    }
    reset({
      ...getValues(),
      requestService: {
        ...getValues("requestService"),
        startOfCare: formatDate(
        reAuthReferralDetailState.startOfCare,
        DateFormats.MM_DD_YYYY
      ),
        inpatientAdmitDate: null,
        inpatientDischargeDate: null,
        visitDate: null,
      },
    });
    callSlots(socDate);
  }, [reAuthReferralDetailState]);

  useEffect(() => {
    setDisciplineOptions(getDisciplineOptions());
  }, [disciplineListState]);

  useEffect(() => {
    reset({
      ...getValues(),
      reAuthTabStatus: updateTabStatus(3, getValues("reAuthTabStatus")),
    });
  }, []);

  const onChangeHandler = (date: any) => {
    const { minDateTime, maxDateTime, selectedTime } = getSelectedTimeForSOC(
      getValue(reAuthReferralDetailState, "startOfCare"),
      date,
      true,
      getValue(memberInfoDataState, "healthPlan")
    );
    if (
      !isNaN(selectedTime) &&
      selectedTime >= minDateTime &&
      selectedTime <= maxDateTime
    ) {
      resetStates(SlotsDispatchTypes.SLOTS_RESET);
      setTimeout(() => {
        callSlots(date);
      }, 200);
    }
  };

  const getNotEligibleForSpeechTherapy = () => {
    if (!isEmpty(slotsState)) {
      const notEligibleForSpeechTherapy =
        is21OrTurning21Soon(
          memberInfoDataState.dob,
          slotsState.currentStartDate,
          slotsState.currentEndDate
        ) ||
        is21OrTurning21Soon(
          memberInfoDataState.dob,
          slotsState.nextStartDate,
          slotsState.nextStartDate
        );

      return notEligibleForSpeechTherapy;
    }
    return false;
  };

  const getDisciplineOptions = () => {
    const options = disciplineListState.filter(
      (singleOption: Discipline) =>
        !(
          singleOption.disciplineId === Numbers.Five &&
          isMedicaid(
            getValue(patientInformationByIdState, "memberInfo.product")
          )
        ) &&
        !(
          !isEmpty(memberInfoDataState.dob) &&
          isAbove21(memberInfoDataState.dob) &&
          singleOption.disciplineId === Numbers.Four &&
          isMedicaid(
            getValue(patientInformationByIdState, "memberInfo.product")
          )
        )
    );

    return options;
  };

  const isMedicaid = (product: string | null) => {
    return product === TextConstants.MEDICAID;
  };

  useEffect(() => {
    if (
      getNotEligibleForSpeechTherapy() &&
      isMedicaid(getValue(patientInformationByIdState, "memberInfo.product"))
    ) {
      setSTWarning(true);
    } else {
      setSTWarning(false);
    }
    reset({
      ...getValues(),
       requestService: {
        ...getValues("requestService"),
        currentStartDate: slotsState.currentStartDate,
        currentEndDate: slotsState.currentEndDate,
        nextStartDate: slotsState.nextStartDate,
        nextEndDate: slotsState.nextEndDate,
      },
    });
}, [slotsState]);

  const isFirstReAuth = getValue(reAuthReferralDetailState, "startOfCareVerified");

  const minimumSOCDate = isFirstReAuth === false &&
    !isMedicaid(
      getValue(patientInformationByIdState, "memberInfo.product")
    ) ? addDaysToDate( getValue(reAuthReferralDetailState, "startOfCare"), -5, "day"
    ) : null;

  const maximumSOCDate = isFirstReAuth === false
    ? addDaysToDate(
      getValue(reAuthReferralDetailState, "startOfCare"), 14, "day"
    ) : null;

  const checkIsBcbsTn = () => {
    return (
      getType({
        label: memberInfoDataState.healthPlan,
        value: memberInfoDataState.payerId,
      }) === Numbers.Four
    );
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        onChangeHandler(value);
      }, 500),
    []
  );

  const getMinimumDate = () => {
    if (getValue(memberInfoDataState, "healthPlan") === BCBSMI) {
      return dayjs(BCBSMIMINSOCDATE);
    }
    return minimumSOCDate;
  };

  return (
    <Grid size={12} pt={2}>
      <Grid container>
        <Grid size={{ xs: 12 }} sx={SectionSpacing}>
          <Grid container sx={flexColumn}>
            <Grid size={{ xs: 12 }} sx={displayFlex}>
              <Typography
                variant="h6"
                fontWeight={fontWeight[5]}
                color={colors.fonts[20]}
                pr={"1rem"}
              >
                REQUEST SERVICES
              </Typography>
              <Divider orientation="vertical" variant="fullWidth" flexItem />
              <Typography
                variant="body1"
                fontWeight={fontWeight[2]}
                color={colors.fonts[2]}
                pl={"1rem"}
              >
                Please enter start of care date, select discipline(s) with home
                health orders and select a care type (if applicable).
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={flexRow}>
        <Grid size={{ xs: 4 }} sx={FieldSpacing}>
          <FormControl variant="standard">
            <DatePicker
              name="requestService.startOfCare"
              control={control}
              label={"Start of Care:"}
              helper={rules.socDateTimeRequired}
              allowFutureDate={true}
              minimumDate={getMinimumDate()}
              maximumdate={maximumSOCDate}
              onChangeHandler={(date: any) => debouncedSearch(date)}
              disabled={
                (reAuthReferralDetailState &&
                  reAuthReferralDetailState.startOfCareVerified) ||
                isMedicaid(
                  getValue(patientInformationByIdState, "memberInfo.product")
                ) || (reAuthReferralDetailState && reAuthReferralDetailState.bcbsTnSOCRetroTrue)
              }
            />
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12 }} sx={{minHeight: '4rem'}}>
          <Grid container>
            <Grid size={{ xs: 6 }} pr={2} pl={2}>
              {InformationText(
                DisciplineTableHeader.MEMBER_ADMITTED,
                fontWeight[4],
                {},
                colors.black[1]
              )}
            </Grid>
            <Grid pl={2} pr={2}>
              <FormControl>
                <Controller
                  name="requestService.memberAdmitted"
                  control={control}
                  defaultValue=""
                  render={({ field }: { field: any }) => (
                    <RadioGroup
                      row
                      {...field}
                      sx={RadioGroupStyle}
                      onChange={(e) => {
                        setDateAdmit(e.target.value === "yes");
                        reset({
                          ...getValues(),
                          requestService: {
                            ...getValues().requestService,
                            memberAdmitted: e.target.value,
                            inpatientAdmitDate: e.target.value === "no" ? null : getValues().requestService.inpatientAdmitDate,
                            inpatientDischargeDate: e.target.value === "no" ? null : getValues().requestService.inpatientDischargeDate,
                          },
                        });
                      }}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Grid>
            {dateAdmit && (
              <>
                <Grid size={{ xs: 2 }} pt={1}>
                  <FormControl variant="standard">
                    <DatePicker
                      name="requestService.inpatientAdmitDate"
                      control={control}
                      label={"Inpatient Admit Date:"}
                      allowFutureDate={false}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 2 }} pt={1}>
                  <FormControl variant="standard">
                    <DatePicker
                      name="requestService.inpatientDischargeDate"
                      control={control}
                      label={"Inpatient Discharge Date:"}
                      allowFutureDate={false}
                    />
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }} sx={{minHeight: '4rem'}}>
          <Grid container>
            <Grid size={{ xs: 6 }} pr={2} pl={2}>
              {InformationText(
                DisciplineTableHeader.MEMBER_EMERGENCY,
                fontWeight[4],
                {},
                colors.black[1]
              )}
            </Grid>
            <Grid pl={2} pr={2}>
              <FormControl>
                <Controller
                  name="requestService.memberVisit"
                  control={control}
                  defaultValue=""
                  render={({ field }: { field: any }) => (
                    <RadioGroup
                      row
                      {...field}
                      sx={RadioGroupStyle}
                      onChange={(e) => {
                        setMemberVisit(e.target.value === "yes");
                        reset({
                          ...getValues(),
                          requestService: {
                            ...getValues().requestService,
                            memberVisit: e.target.value,
                            visitDate: e.target.value === "no" ? null : getValues().requestService.visitDate,
                          },
                        });
                      }}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Grid>
            {memberVisit && (
              <>
                <Grid size={{ xs: 2 }} pt={1}>
                  <FormControl variant="standard">
                    <DatePicker
                      name="requestService.visitDate"
                      control={control}
                      label={"Visit Date:"}
                      allowFutureDate={false}
                    />
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        {(checkIsBcbsTn()) && (
          <Grid size={{ xs: 12 }} mr={2.5} pr={2} pb={2} sx={StyleGridRow}>
            <FaExclamationIcon />
            {InformationText(
              TextConstants.BCBS_TN_WARNING,
              fontWeight[5],
              {},
              colors.black[1],
            )}
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <DisciplineTable
            getValues={getValues}
            reset={reset}
            control={control}
            disciplineListState={disciplineOptions}
            stWarning={stWarning}
            slotsState={slotsState}
            memberInfoDataState={memberInfoDataState}
            isBcbsTn={checkIsBcbsTn()}
            disciplineStatusState={disciplineStatusState}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RequestServices;